if (!customElements.get('wdd-cart-upsell')) {
  class WddCartUpsell extends HTMLElement {
    constructor() {
      super();

      this.refreshTimer = null;
      this.refreshController = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleCartChange = this.handleCartChange.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.handleClick);
      document.addEventListener('cart:change', this.handleCartChange);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('cart:change', this.handleCartChange);
      window.clearTimeout(this.refreshTimer);
      this.refreshController?.abort();
    }

    handleCartChange() {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => this.refreshSection(), 150);
    }

    handleClick(event) {
      const button = event.target.closest('[data-wdd-upsell-add]');

      if (!button || !this.contains(button)) return;

      event.preventDefault();
      this.addProduct(button);
    }

    async refreshSection() {
      this.refreshController?.abort();
      this.refreshController = new AbortController();

      try {
        const url = new URL(this.dataset.sectionUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(url.toString(), {
          headers: { Accept: 'text/html' },
          signal: this.refreshController.signal
        });

        if (!response.ok) throw new Error(`Section refresh failed: ${response.status}`);

        const html = await response.text();
        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const nextSection = parsedDocument.querySelector('wdd-cart-upsell');

        if (nextSection) this.replaceWith(nextSection);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('WDD cart upsell refresh failed:', error);
        }
      }
    }

    async addProduct(button) {
      if (button.getAttribute('aria-disabled') === 'true') return;

      const variantId = Number(button.dataset.variantId);
      const mainCartItems = document.getElementById('main-cart-items');
      const mainSectionId = mainCartItems?.dataset.id;

      if (!variantId || !mainSectionId) {
        this.showError(button, this.dataset.errorMessage);
        return;
      }

      this.setLoading(button, true);
      this.showError(button, '');

      try {
        const response = await fetch(routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: [mainSectionId, 'cart-icon-bubble'],
            sections_url: routes.cart_url
          })
        });
        const responseBody = await response.json();

        if (!response.ok || responseBody.status) {
          throw new Error(responseBody.description || responseBody.message || this.dataset.errorMessage);
        }

        this.replaceRenderedSection(
          responseBody.sections?.[mainSectionId],
          '.main-cart'
        );
        this.replaceRenderedSection(
          responseBody.sections?.['cart-icon-bubble'],
          '#cart-icon-bubble'
        );

        const cartResponse = await fetch(`${routes.cart_url}.js`, {
          headers: { Accept: 'application/json' }
        });

        if (!cartResponse.ok) throw new Error(this.dataset.errorMessage);

        const cart = await cartResponse.json();

        document.dispatchEvent(
          new CustomEvent('variant:add', {
            detail: {
              variant: { id: String(variantId) },
              quantity: 1,
              sectionId: this.dataset.sectionId
            }
          })
        );
        document.dispatchEvent(
          new CustomEvent('cart:change', {
            detail: {
              cart,
              sectionId: this.dataset.sectionId
            }
          })
        );
      } catch (error) {
        this.showError(button, error.message || this.dataset.errorMessage);
      } finally {
        if (button.isConnected) this.setLoading(button, false);
      }
    }

    replaceRenderedSection(html, selector) {
      if (!html) return;

      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const source = parsedDocument.querySelector(selector);
      const target = document.querySelector(selector);

      if (source && target) target.replaceWith(source);
    }

    setLoading(button, isLoading) {
      button.disabled = isLoading;
      button.setAttribute('aria-disabled', String(isLoading));
      button.setAttribute('aria-busy', String(isLoading));
      button.classList.toggle('is-loading', isLoading);

      const label = button.querySelector('[data-wdd-upsell-button-label]');
      const loadingLabel = button.querySelector('[data-wdd-upsell-loading-label]');

      if (label) label.hidden = isLoading;
      if (loadingLabel) loadingLabel.hidden = !isLoading;
    }

    showError(button, message) {
      const actions = button.closest('.wdd-cart-upsell__actions') || this;
      const status = actions.querySelector('[data-wdd-upsell-status]');

      if (status) status.textContent = message || '';
    }
  }

  customElements.define('wdd-cart-upsell', WddCartUpsell);
}
